import { useI18n } from "@core/i18n/i18n";
import { usePatientNames } from "@domain/patient/hooks/use-patients";
import { PatientNameListItem } from "@domain/patient/patient";
import styled from "@emotion/styled";
import { ErrorMessage } from "@ui/components/common/error-message";
import { HighlightedString } from "@ui/components/inputs/highlighted-string";
import { LoadingContainer } from "@ui/components/common/loading-container";
import { spacings } from "@ui/style";
import { colors } from "@ui/style/colors";
import { isNotNil } from "@utils/assert";
import { formatName, removeDiacriticsFromString } from "@utils/string-utils";
import _ from "lodash";
import { AutoComplete } from "primereact/autocomplete";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";

export type PatientWithName = { id: string; analyticsId: string; name: string };

type SelectablePatientItem = PatientWithName & { selectable: true };
type PatientItem = SelectablePatientItem | { name: string; selectable: false };

function isSelectablePatientItem(patientItem: PatientItem): patientItem is SelectablePatientItem {
	return patientItem.selectable;
}

interface PatientAutocompleteProps<T extends FieldValues> {
	control: Control<T, object>;
	name: Path<T>;
}

export const PatientAutocomplete = <T extends FieldValues>({
	control,
	name,
}: PatientAutocompleteProps<T>): JSX.Element => {
	const { patientNames, loading } = usePatientNames();
	const { format } = useI18n();
	const [query, setQuery] = useState("");

	const autoCompleteRef = useRef<AutoComplete>(null);

	const allPatientsWithName = useMemo<PatientItem[]>(
		() =>
			patientNames
				.map((patient: PatientNameListItem) => ({
					id: patient.id,
					analyticsId: patient.analyticsId,
					name: formatName(patient),
					selectable: true,
				}))
				.sort((p1, p2) => p1.name.localeCompare(p2.name))
				.filter(isSelectablePatientItem),
		[patientNames],
	);

	const findPatientWithNameFromString = useCallback(
		(searchedValue: string) =>
			allPatientsWithName.find(
				patient =>
					removeDiacriticsFromString(patient.name).toLowerCase().trim() ===
					removeDiacriticsFromString(searchedValue).toLowerCase().trim(),
			),
		[allPatientsWithName],
	);

	const filteredPatients = useMemo<PatientItem[]>(() => {
		const trimmedPatients = allPatientsWithName.filter(patient => {
			const trimmedQuery = removeDiacriticsFromString(query).toLowerCase().trim();
			const patientName = removeDiacriticsFromString(patient.name).toLowerCase().trim();
			return _.isEmpty(trimmedQuery) ? true : patientName.includes(trimmedQuery);
		});
		return _.isEmpty(trimmedPatients)
			? [
					{
						name: format("templateBuilder.assignTemplateDialog.patientInput.emptyList"),
						selectable: false,
					},
				]
			: trimmedPatients;
	}, [allPatientsWithName, format, query]);

	const itemRenderTemplate = useCallback(
		(currentPatient: PatientItem) => {
			if (loading) {
				return <LoadingContainer style={{ height: 20 }} />;
			}

			if (currentPatient.selectable) {
				return <DropDownItem name={currentPatient.name} queryString={query} />;
			}

			return currentPatient.name;
		},
		[loading, query],
	);

	return (
		<Controller
			name={name}
			control={control}
			rules={{
				required: format("global.form.field.requiredError"),
				validate: (value: PatientWithName | string | null) => {
					if (typeof value === "string") {
						return format("templateBuilder.assignTemplateDialog.patientInput.incorrectPatient");
					}
				},
			}}
			render={({ field, fieldState }) => (
				<>
					<StyledAutoComplete
						{...field}
						inputClassName={fieldState.error ? "p-invalid" : undefined}
						onChange={e => {
							if (typeof e.value === "string") {
								setQuery(e.value);
								const patient = findPatientWithNameFromString(e.value);
								if (isNotNil(patient)) {
									field.onChange(patient);
								} else {
									field.onChange(e);
								}
							} else {
								const selectedPatient = e.value as PatientItem;
								if (selectedPatient.selectable) {
									field.onChange(e);
								}
							}
						}}
						ref={autoCompleteRef}
						onClick={() => autoCompleteRef.current?.show()}
						onClear={() => autoCompleteRef.current?.show()}
						onFocus={() => {
							if (typeof field.value === "string") {
								autoCompleteRef.current?.show();
							}
						}}
						suggestions={filteredPatients}
						field="name"
						itemTemplate={itemRenderTemplate}
						placeholder={format("templateBuilder.assignTemplateDialog.patientInput.placeholder")}
						panelFooterTemplate={AutoCompleteFooter}
						panelStyle={{ maxWidth: 400 }}
					/>
					<ErrorMessage>{fieldState.error?.message}</ErrorMessage>
				</>
			)}
		/>
	);
};

const AutoCompleteFooter = (): JSX.Element => {
	const { format } = useI18n();
	return (
		<StyledFooter>
			<FooterText className="p-size-xs">
				{format("templateBuilder.assignTemplateDialog.patientInput.footerMessage")}
			</FooterText>
		</StyledFooter>
	);
};
const FooterText = styled.div`
	color: ${colors.grey80};
`;

const StyledFooter = styled.div`
	border-top: 1px solid ${colors.grey25};
	padding: 12px ${spacings.S};
`;

const StyledAutoComplete = styled(AutoComplete)`
	input {
		width: 100%;
		border-radius: 0.5rem !important;
	}
`;

interface DropDownItemProps {
	name: string;
	queryString: string;
}

const DropDownItem = ({ name, queryString }: DropDownItemProps): JSX.Element => (
	<div className="p-size-s" style={{ color: colors.grey }}>
		<HighlightedString text={name} queryString={queryString} />
	</div>
);
